import { useMutation } from '@apollo/client';
import { TrashIcon } from 'lucide-react';
import { useState } from 'react';

import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import { TooltipLabel } from '@eluve/blocks';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

import { getAdminTenantsSummaryFilterQuery } from './query';

interface DeleteTenantActionProps {
  tenantId: string;
}

const deleteTenantMutation = graphql(`
  mutation deleteTenant($tenantId: uuid!, $deletedAt: timestamptz!) {
    updateAdminTenants(
      where: { id: { _eq: $tenantId } }
      _set: { deletedAt: $deletedAt }
    ) {
      returning {
        __typename
        id
        deletedAt
      }
    }
  }
`);

const adminTenantUsersCount = graphql(`
  fragment UserCount on AdminTenants {
    __typename
    id
    userCount
  }
`);

export const DeleteTenantAction: React.FC<DeleteTenantActionProps> = ({
  tenantId,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteTenant, { loading }] = useMutation(deleteTenantMutation, {
    refetchQueries: [getAdminTenantsSummaryFilterQuery],
  });
  const toast = useToast();

  const tenantUsers =
    useCompleteFragment({
      fragment: adminTenantUsersCount,
      key: { id: tenantId },
    })?.userCount ?? 0;

  const handleDeleteTenant = async () => {
    deleteTenant({
      variables: {
        tenantId,
        deletedAt: new Date().toISOString(),
      },
      onCompleted: () => {
        setIsDialogOpen(false);
      },
      onError: () => {
        toast.error('Failed to delete organization');
      },
      update: (cache) => {
        cacheUtils.evict({
          typeName: 'Tenants',
          key: { id: tenantId },
        });
        cache.gc();
      },
    });
  };

  if (tenantUsers === undefined || tenantUsers > 0) {
    return null;
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen} modal>
      <DialogTrigger asChild>
        <Button variant="destructive" size="icon">
          <TooltipLabel label="Delete Organization">
            <TrashIcon />
          </TooltipLabel>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Organization</DialogTitle>
        </DialogHeader>
        <p>Are you sure you want to soft delete this organization?</p>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setIsDialogOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteTenant}
            variant="destructive"
            disabled={loading}
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
